import React, { useState, useEffect } from 'react'
import {
  Typography,
  Box,
  Paper,
  Grid,
  Divider,
  Chip,
  TextField,
  Tooltip
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import {
  CheckCircle,
  Description,
  Business,
  Warning,
  Cancel,
  Edit,
  Save,
  Close,
  TrendingUp,
  TrendingDown,
  Security,
  Gavel,
  PictureAsPdf,
  Info
} from '@material-ui/icons'
import Button from '../../../../shared/Button'
import Loader from 'components/shared/Loader'
import { creditMemoDraftFileName } from 'tools/creditMemoFileName'
import { Toast } from 'components/shared/Toaster'
import { requestCreditMemo, getCreditMemo, updateCreditMemoSummary, updateCreditMemoRiskScore, getCreditMemoStatus, getAvailableSummary, downloadCreditMemoPdf } from 'actions/applicants'
import CreditMemoApprovalPanel from './CreditMemoApprovalPanel'
import { processStarted } from 'constants/utilityFunctions'
import strings from 'strings'
import AuthService from 'tools/AuthService'
import moment from 'moment'
import CreditCost from 'components/shared/CreditCost'

const useStyles = makeStyles((theme) => ({
  container: {
    padding: theme.spacing(3),
    minHeight: '500px',
    maxWidth: '1400px',
    margin: '0 auto'
  },
  header: {
    marginBottom: theme.spacing(4)
  },
  headerTop: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: theme.spacing(2)
  },
  title: {
    fontWeight: 700,
    fontSize: '2.5rem',
    color: theme.palette.text.primary
  },
  subtitle: {
    color: theme.palette.text.secondary,
    fontSize: '1rem',
    marginTop: theme.spacing(0.5)
  },
  buttonGroup: {
    display: 'flex',
    gap: theme.spacing(1),
    alignItems: 'center'
  },
  dateBadge: {
    backgroundColor: theme.palette.grey[100],
    color: theme.palette.text.primary,
    fontSize: '0.875rem'
  },
  companyCard: {
    marginBottom: theme.spacing(3),
    borderRadius: theme.spacing(1),
    border: `1px solid ${theme.palette.grey[300]}`
  },
  companyHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2)
  },
  companyIcon: {
    padding: theme.spacing(1),
    backgroundColor: `${theme.palette.primary.main}10`,
    borderRadius: theme.spacing(1),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  companyTitle: {
    fontSize: '1.5rem',
    fontWeight: 600,
    color: theme.palette.text.primary
  },
  companySubtitle: {
    fontSize: '1rem',
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.5)
  },
  infoGrid: {
    marginTop: theme.spacing(3)
  },
  infoItem: {
    '& .label': {
      fontSize: '0.875rem',
      fontWeight: 500,
      color: theme.palette.text.secondary,
      marginBottom: theme.spacing(0.5),
      display: 'block'
    },
    '& .value': {
      fontSize: '1.125rem',
      fontWeight: 600,
      color: theme.palette.text.primary,
      display: 'block'
    }
  },
  flagsSection: {
    marginTop: theme.spacing(3)
  },
  flagsTitle: {
    fontSize: '0.875rem',
    fontWeight: 600,
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(1.5),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1)
  },
  flagsContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1)
  },
  kpiGrid: {
    marginBottom: theme.spacing(3)
  },
  kpiCard: {
    padding: theme.spacing(2),
    borderRadius: theme.spacing(1),
    border: `1px solid ${theme.palette.grey[300]}`,
    height: '100%'
  },
  kpiHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
    color: theme.palette.text.secondary,
    fontSize: '0.875rem'
  },
  kpiValue: {
    fontSize: '2.5rem',
    fontWeight: 700,
    lineHeight: 1,
    marginBottom: theme.spacing(1)
  },
  kpiTrend: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    fontSize: '0.875rem',
    fontWeight: 500,
    marginBottom: theme.spacing(1)
  },
  kpiDescription: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary
  },
  kpiReason: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary
  },
  progressBar: {
    height: 8,
    borderRadius: 4,
    backgroundColor: theme.palette.grey[200],
    marginBottom: theme.spacing(1),
    overflow: 'hidden'
  },
  progressFill: {
    height: '100%',
    transition: 'width 0.3s ease'
  },
  riskAssessmentCard: {
    marginBottom: theme.spacing(3),
    borderRadius: theme.spacing(1),
    border: `1px solid ${theme.palette.grey[300]}`
  },
  sectionTitle: {
    fontSize: '1.25rem',
    fontWeight: 600,
    color: theme.palette.text.primary
  },
  sectionSubtitle: {
    fontSize: '1rem',
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.5)
  },
  subsectionTitle: {
    fontSize: '0.875rem',
    fontWeight: 600,
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(2),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1)
  },
  riskItemCard: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(2),
    borderRadius: theme.spacing(1),
    border: `1px solid ${theme.palette.grey[300]}`,
    backgroundColor: theme.palette.background.paper,
    transition: 'background-color 0.2s ease',
    '&:hover': {
      backgroundColor: theme.palette.action.hover
    }
  },
  riskItemLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    flex: 1
  },
  riskItemContent: {
    flex: 1
  },
  riskItemName: {
    fontSize: '0.875rem',
    fontWeight: 500,
    color: theme.palette.text.primary
  },
  riskItemEntity: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.25)
  },
  statusBadge: {
    fontWeight: 500,
    fontSize: '0.75rem'
  },
  greenStatus: {
    backgroundColor: `${theme.palette.success.main}15`,
    color: theme.palette.success.main,
    border: `1px solid ${theme.palette.success.main}30`
  },
  orangeStatus: {
    backgroundColor: `${theme.palette.warning.main}15`,
    color: theme.palette.warning.main,
    border: `1px solid ${theme.palette.warning.main}30`
  },
  redStatus: {
    backgroundColor: `${theme.palette.error.main}15`,
    color: theme.palette.error.main,
    border: `1px solid ${theme.palette.error.main}30`
  },
  concernsGrid: {
    marginTop: theme.spacing(3)
  },
  concernCard: {
    padding: theme.spacing(2),
    borderRadius: theme.spacing(1),
    height: '100%'
  },
  criticalConcern: {
    backgroundColor: `${theme.palette.error.main}05`,
    border: `1px solid ${theme.palette.error.main}20`
  },
  positiveFactor: {
    backgroundColor: `${theme.palette.success.main}05`,
    border: `1px solid ${theme.palette.success.main}20`
  },
  concernHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2)
  },
  concernTitle: {
    fontSize: '0.875rem',
    fontWeight: 600
  },
  concernList: {
    listStyle: 'none',
    padding: 0,
    margin: 0,
    '& li': {
      display: 'flex',
      alignItems: 'flex-start',
      gap: theme.spacing(1),
      marginBottom: theme.spacing(1),
      fontSize: '0.875rem',
      color: theme.palette.text.secondary,
      '& .bullet': {
        marginTop: '2px',
        fontSize: '1rem',
        fontWeight: 'bold'
      }
    }
  },
  executiveSummaryCard: {
    borderRadius: theme.spacing(1),
    border: `1px solid ${theme.palette.grey[300]}`
  },
  executiveSummaryHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between'
  },
  editButtons: {
    display: 'flex',
    gap: theme.spacing(1)
  },
  summaryContent: {
    '& p': {
      fontSize: '1rem',
      lineHeight: 1.6,
      color: theme.palette.text.primary,
      marginBottom: theme.spacing(2),
      '&:last-child': {
        marginBottom: 0
      }
    }
  },
  summaryTextfield: {
    marginBottom: theme.spacing(2),
    '& .MuiOutlinedInput-root': {
      fontSize: '0.875rem'
    }
  },
  overallAssessment: {
    padding: theme.spacing(2),
    backgroundColor: theme.palette.grey[50],
    borderRadius: theme.spacing(1),
    marginTop: theme.spacing(3)
  },
  assessmentText: {
    fontSize: '1rem',
    lineHeight: 1.6,
    color: theme.palette.text.primary,
    '& strong': {
      fontWeight: 600
    }
  },
  cardPadding: {
    padding: theme.spacing(3)
  },
  riskFactorsContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    color: theme.palette.text.secondary,
    fontSize: '0.75rem',
    cursor: 'pointer',
    marginTop: theme.spacing(1),
    '& .MuiSvgIcon-root': {
      fontSize: '0.875rem'
    }
  },
  tooltipList: {
    listStyle: 'none',
    padding: 0,
    margin: 0,
    '& li': {
      padding: theme.spacing(0.25, 0),
      fontSize: '0.75rem',
      '&:before': {
        content: '"•"',
        marginRight: theme.spacing(1),
        fontWeight: 'bold'
      }
    }
  },
  darkTooltip: {
    backgroundColor: '#000000',
    color: 'var(--bg-surface)',
    fontSize: '0.75rem',
    '& .MuiTooltip-arrow': {
      color: '#000000',
    }
  }
}))

// Mock JSON payload structure - this would come from a GET API
// Function to transform API response to component format
const transformApiResponseToComponentData = (apiResponse) => {
  const currentDate = moment().format('DD-MM-YYYY')

  // Transform key findings to KPIs format
  const transformedKpis = apiResponse.keyFindings?.map((finding, index) => {
    const kpiMap = {
      'Risk Score': {
        title: finding.name,
        value: finding.score?.toString() || '0',
        suffix: '/100',
        description: 'Risk Assessment Score',
        progress: finding.score || 0,
        icon: 'scale',
        reasons: finding.reasons
      },
      'Active Recorded Liens and Judgments': {
        title: 'Active Liens',
        value: finding.quantity?.toString() || '0',
        trend: finding.concerning ? 'error' : 'success',
        trendLabel: finding.concerning ? 'High' : 'Low',
        description: 'Liens & Judgements Recorded',
        color: finding.concerning ? 'error' : 'success',
        icon: 'trending_up'
      },
      'UCC Filings': {
        title: finding.name,
        value: finding.quantity?.toString() || '0',
        trend: finding.quantity === 0 ? 'success' : finding.quantity > 2 ? 'error' : 'success',
        trendLabel: finding.quantity === 0
            ? 'None Found'
            : finding.quantity > 1
                ? 'Multiple Filings'
                : 'Active Filings',
        description: finding.quantity === 0
            ? 'No UCC filings found. No financial encumbrances detected.'
            : finding.quantity > 2
                ? 'Multiple UCC filings may indicate financial risk or obligations.'
                : 'Active UCC filing indicate ongoing financial engagements.',
        color: finding.quantity === 0 ? 'success' : finding.quantity > 2 ? 'error' : 'success',
        icon: 'check_circle'
      }
      ,
      'Bankruptcies': {
        title: finding.name,
        value: finding.quantity?.toString() || '0',
        trend: finding.quantity > 0 ? 'error' : 'success',
        trendLabel: finding.quantity > 0 ? 'Bankruptcies Found' : 'None',
        description: finding.quantity > 0
            ? 'Recent bankruptcies indicate financial distress.'
            : 'No financial distress detected.',
        color: finding.quantity > 0 ? 'error' : 'success',
        icon: 'trending_down'
      }

    }

    return kpiMap[finding.name] || {
      title: finding.name,
      value: finding.score?.toString() || finding.quantity?.toString() || '0',
      description: 'Key Finding',
      icon: 'check_circle'
    }
  }) || []

  return {
    report: {
      businessName: apiResponse.businessName || 'Business Name',
      entityType: apiResponse.entityType || 'Unknown',
      industry: apiResponse.businessNature || 'Unknown',
      businessStartDate: apiResponse.businessStartDate || 'Unknown', // Not provided in API response
      primaryLocation: apiResponse.primaryLocation || 'Unknown',
      businessNature: apiResponse.businessNature || 'Unknown',
      generatedDate: currentDate
    },
    kpis: transformedKpis,
    riskAssessment: {
      primaryRisk: apiResponse.riskAssessmentInsights?.summary || [],
      additionalRisk: apiResponse.riskAssessmentInsights?.additionalSummary || []
    },
    executiveSummary: {
      content: apiResponse.executiveSummary || 'No executive summary available'
    }
  }
}

const getStatusConfig = (status) => {
  switch (status) {
    case 'GREEN':
      return {
        color: 'success',
        icon: CheckCircle,
        bgColor: 'var(--status-approved)',
        textColor: '#1b5e20'
      }
    case 'ORANGE':
      return {
        color: 'warning',
        icon: Warning,
        bgColor: 'var(--status-pending)',
        textColor: '#e65100'
      }
    case 'RED':
      return {
        color: 'error',
        icon: Cancel,
        bgColor: 'var(--status-declined)',
        textColor: 'var(--status-declined)'
      }
    default:
      return {
        color: 'default',
        icon: Description,
        bgColor: 'var(--fg-tertiary)',
        textColor: '#424242'
      }
  }
}

const formatDate = (date) => {
  if (!date) return 'Unknown'
  const momentDate = moment(date)
  return momentDate.isValid() ? momentDate.format('DD-MM-YYYY') : date
}

const CreditMemo = ({ applicantId, creditCosts, updates }) => {
  const classes = useStyles()
  const [loading, setLoading] = useState(false)
  // True while the request itself runs: the backend generates the whole memo before it answers.
  const [requesting, setRequesting] = useState(false)
  // The memo's business name as the API returned it (the display data falls back to a placeholder).
  const [businessName, setBusinessName] = useState(null)
  const [creditMemoData, setCreditMemoData] = useState(null)
  const [showCreditMemo, setShowCreditMemo] = useState(false)
  const [isEditingExecutiveSummary, setIsEditingExecutiveSummary] = useState(false)
  const [editedSummary, setEditedSummary] = useState('')
  const [isEditingRiskScore, setIsEditingRiskScore] = useState(false)
  const [editedRiskScore, setEditedRiskScore] = useState('')
  const [creditMemoStatus, setCreditMemoStatus] = useState(null)
  const [isCheckingStatus, setIsCheckingStatus] = useState(false)
  const [initialLoading, setInitialLoading] = useState(true) // New state for initial load
  const [hasSavedReports, setHasSavedReports] = useState(false) // At least one report saved for this applicant
  const [loadError, setLoadError] = useState(false)
  const [approvalLocked, setApprovalLocked] = useState(false) // A version is waiting for approval: no edits
  const [exportingPdf, setExportingPdf] = useState(false)

  const { creditUsageEnabled, creditMemoApprovalEnabled } = AuthService.getClientFeatures()

  // Once a version is frozen for approval, close any edit that was already open so its Save can't go through
  // (the backend would refuse it with a 409 anyway). Edited text is discarded, back to the saved values.
  useEffect(() => {
    if (!approvalLocked) return
    setIsEditingExecutiveSummary(false)
    setIsEditingRiskScore(false)
    if (creditMemoData) {
      setEditedSummary(creditMemoData.executiveSummary?.content || '')
      const riskScoreKpi = creditMemoData.kpis?.find(kpi => kpi.title.includes('Risk Score'))
      if (riskScoreKpi) setEditedRiskScore(riskScoreKpi.value)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [approvalLocked])

  // Get the credit cost for CREDIT_MEMO request type
  const getCreditCost = () => {
    if (creditCosts) {
      const costObj = creditCosts.find(cost => cost.requestType === 'CREDIT_MEMO')
      return costObj ? costObj.creditCost : 0
    }
    return 0
  }

  // Check credit memo status when component mounts
  useEffect(() => {
    if (!applicantId) {
      setInitialLoading(false)
      return
    }

    let interval = null

    const checkCreditMemoStatus = async () => {
      try {
        const statusResponse = await getCreditMemoStatus(applicantId)()
        const status = statusResponse?.status || 'NOT_STARTED'
        setCreditMemoStatus(status)

        if (status === 'COMPLETED') {
          setShowCreditMemo(true)
          setIsCheckingStatus(false)
          // Clear any existing interval
          if (interval) {
            clearInterval(interval)
            interval = null
          }
        } else if (status === 'IN_PROGRESS') {
          setIsCheckingStatus(true)
          setShowCreditMemo(false)
          // Start polling every 2 seconds
          if (!interval) {
            interval = setInterval(async () => {
              try {
                const newStatusResponse = await getCreditMemoStatus(applicantId)()
                const newStatus = newStatusResponse?.status || 'NOT_STARTED'
                setCreditMemoStatus(newStatus)

                if (newStatus === 'COMPLETED') {
                  setShowCreditMemo(true)
                  setIsCheckingStatus(false)
                  clearInterval(interval)
                  interval = null
                } else if (newStatus === 'NOT_STARTED' || newStatus === 'THREAD_FAILED' || newStatus?.includes('ERROR')) {
                  setShowCreditMemo(false)
                  setIsCheckingStatus(false)
                  clearInterval(interval)
                  interval = null
                }
              } catch (pollError) {
                console.error('Error polling credit memo status:', pollError)
                setIsCheckingStatus(false)
                setShowCreditMemo(false)
                clearInterval(interval)
                interval = null
              }
            }, 2000)
          }
        } else {
          // NOT_STARTED, THREAD_FAILED, or other error status
          setShowCreditMemo(false)
          setIsCheckingStatus(false)
        }
      } catch (error) {
        console.error('Error checking credit memo status:', error)
        setShowCreditMemo(false)
        setIsCheckingStatus(false)
        setCreditMemoStatus('NOT_STARTED')
      } finally {
        setInitialLoading(false)
      }
    }

    checkCreditMemoStatus()

    // Cleanup interval on component unmount
    return () => {
      if (interval) {
        clearInterval(interval)
      }
    }
  }, [applicantId])

  // Determine whether the applicant has at least one saved report.
  // A credit memo can only be requested once there is something to base it on.
  useEffect(() => {
    if (!applicantId) return

    const checkSavedReports = async () => {
      try {
        const reports = await getAvailableSummary(applicantId)()
        setHasSavedReports(processStarted(reports || []))
      } catch (error) {
        console.error('Error checking saved reports for credit memo:', error)
        setHasSavedReports(false)
      }
    }

    checkSavedReports()
  }, [applicantId, updates])

  // Start status checking when creditMemoStatus changes to IN_PROGRESS
  useEffect(() => {
    if (!applicantId || creditMemoStatus !== 'IN_PROGRESS') return

    let interval = null

    // Start polling immediately when status is set to IN_PROGRESS
    interval = setInterval(async () => {
      try {
        const statusResponse = await getCreditMemoStatus(applicantId)()
        const status = statusResponse?.status || 'NOT_STARTED'
        setCreditMemoStatus(status)

        if (status === 'COMPLETED') {
          console.log('Status check: Credit memo completed, showing report...')
          setShowCreditMemo(true)
          setIsCheckingStatus(false)
          clearInterval(interval)
        } else if (status === 'NOT_STARTED' || status === 'THREAD_FAILED' || status?.includes('ERROR')) {
          setShowCreditMemo(false)
          setIsCheckingStatus(false)
          clearInterval(interval)
        }
      } catch (pollError) {
        console.error('Error polling credit memo status:', pollError)
        setIsCheckingStatus(false)
        setShowCreditMemo(false)
        clearInterval(interval)
      }
    }, 2000)

    // Cleanup interval when status changes or component unmounts
    return () => {
      if (interval) {
        clearInterval(interval)
      }
    }
  }, [applicantId, creditMemoStatus])

  useEffect(() => {
    // Only fetch data when credit memo is requested
    if (applicantId && showCreditMemo) {
      setLoading(true)

      // Fetch credit memo data from the new API
      const fetchCreditMemoData = async () => {
        setLoadError(false)
        try {
          const apiResponse = await getCreditMemo(applicantId)()

          // Transform API response to component format
          const transformedData = transformApiResponseToComponentData(apiResponse)
          setCreditMemoData(transformedData)
          setBusinessName(apiResponse.businessName || null)

          // Use original summary from credit memo API
          setEditedSummary(apiResponse.executiveSummary || transformedData.executiveSummary.content)

          const riskScoreKpi = transformedData.kpis.find(kpi => kpi.title.includes('Risk Score'))
          if (riskScoreKpi) {
            setEditedRiskScore(riskScoreKpi.value)
          }

        } catch (error) {
          console.error('Error fetching credit memo data:', error)
          setLoadError(true)
        } finally {
          setLoading(false)
        }
      }

      fetchCreditMemoData()
      console.log('Fetching credit memo data for applicant:', applicantId)
    }
  }, [applicantId, showCreditMemo])

  const handleRequestCreditMemo = async () => {
    setRequesting(true)

    let firstAttemptError = null
    let success = false

    try {
      await requestCreditMemo(applicantId)()
      success = true
    } catch (error) {
      console.error('Error requesting credit memo (first attempt):', error)
      firstAttemptError = error
    }

    if (success) {
      Toast({
        type: 'success',
        message: strings.APPLICANT.CREDIT_MEMO.CREDIT_MEMO_REQUEST_SUCCESS
      })
      console.log('Credit memo request successful, starting status checking...')
      setIsCheckingStatus(true)
      setCreditMemoStatus('IN_PROGRESS')
      setRequesting(false)
      return
    }

    // First attempt failed - check if it's a 500 error and retry
    if (firstAttemptError && (firstAttemptError.status === 500 || firstAttemptError.response?.status === 500)) {
      try {
        console.log('Retrying credit memo request due to 500 error...')
        await requestCreditMemo(applicantId)()
        // Retry succeeded
        Toast({
          type: 'success',
          message: strings.APPLICANT.CREDIT_MEMO.CREDIT_MEMO_REQUEST_SUCCESS
        })
        console.log('Credit memo retry successful, starting status checking...')
        setIsCheckingStatus(true)
        setCreditMemoStatus('IN_PROGRESS')
      } catch (retryError) {
        console.error('Error requesting credit memo (second attempt):', retryError)
        // Both attempts failed
        Toast({
          type: 'error',
          message: strings.APPLICANT.CREDIT_MEMO.CREDIT_MEMO_REQUEST_ERROR
        })
        setShowCreditMemo(false)
        setIsCheckingStatus(false)
        setCreditMemoStatus('NOT_STARTED')
      }
    } else {
      // Non-500 error, show error immediately
      Toast({
        type: 'error',
        message: strings.APPLICANT.CREDIT_MEMO.CREDIT_MEMO_REQUEST_ERROR
      })
      setShowCreditMemo(false)
      setIsCheckingStatus(false)
      setCreditMemoStatus('NOT_STARTED')
    }

    setRequesting(false)
  }

  const handleSave = async () => {
    if (approvalLocked) return
    try {
      await updateCreditMemoSummary(applicantId, editedSummary)()
      setCreditMemoData(prev => ({
        ...prev,
        executiveSummary: {
          content: editedSummary
        }
      }))
      setIsEditingExecutiveSummary(false)
      Toast({
        type: 'success',
        message: strings.APPLICANT.CREDIT_MEMO.EXECUTIVE_SUMMARY_UPDATE_SUCCESS || 'Executive summary updated successfully'
      })
    } catch (error) {
      console.error('Error updating executive summary:', error)
      Toast({
        type: 'error',
        message: strings.APPLICANT.CREDIT_MEMO.EXECUTIVE_SUMMARY_UPDATE_ERROR || 'Failed to update executive summary'
      })
    }
  }

  const handleCancel = () => {
    setEditedSummary(creditMemoData.executiveSummary.content)
    setIsEditingExecutiveSummary(false)
  }

  const handleSaveRiskScore = async () => {
    if (approvalLocked) return
    try {
      // Validate risk score range
      const score = parseInt(editedRiskScore)
      if (isNaN(score) || score < 0 || score > 100) {
        Toast({
          type: 'error',
          message: 'Risk score must be a number between 0 and 100'
        })
        return
      }

      // Call API to update risk score
      await updateCreditMemoRiskScore(applicantId, { riskScore: score })()

      // Update the risk score in the local data
      setCreditMemoData(prev => ({
        ...prev,
        kpis: prev.kpis.map(kpi =>
          kpi.title.includes('Risk Score')
            ? { ...kpi, value: score.toString(), progress: score }
            : kpi
        )
      }))
      setIsEditingRiskScore(false)
      Toast({
        type: 'success',
        message: strings.APPLICANT.CREDIT_MEMO.RISK_SCORE_UPDATE_SUCCESS || 'Risk score updated successfully'
      })
    } catch (error) {
      console.error('Error updating risk score:', error)
      Toast({
        type: 'error',
        message: strings.APPLICANT.CREDIT_MEMO.RISK_SCORE_UPDATE_ERROR || 'Failed to update risk score'
      })
    }
  }

  const handleCancelRiskScore = () => {
    const riskScoreKpi = creditMemoData.kpis.find(kpi => kpi.title.includes('Risk Score'))
    if (riskScoreKpi) {
      setEditedRiskScore(riskScoreKpi.value)
    }
    setIsEditingRiskScore(false)
  }

  const handleExportPdf = async () => {
    setExportingPdf(true)
    try {
      await downloadCreditMemoPdf(applicantId, creditMemoDraftFileName(businessName))
    } catch (error) {
      console.error('Error exporting credit memo PDF:', error)
    } finally {
      setExportingPdf(false)
    }
  }

  const processingScreen = (
    <Box className={classes.container}>
      <Box
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '400px',
          textAlign: 'center'
        }}
      >
        <Typography variant="h4" style={{ marginBottom: '16px', fontWeight: 600 }}>
          {strings.APPLICANT.CREDIT_MEMO.REQUESTING_CREDIT_MEMO}
        </Typography>
        <Typography
          variant="body1"
          style={{
            marginBottom: '32px',
            color: '#666',
            maxWidth: '500px',
            lineHeight: 1.6
          }}
        >
          {strings.APPLICANT.CREDIT_MEMO.PROCESSING_DESCRIPTION}
        </Typography>
        <Box style={{ marginBottom: '32px' }}>
          <Loader />
        </Box>
        <Typography
          variant="body2"
          style={{
            color: '#999',
            fontSize: '0.875rem'
          }}
        >
          Status: {requesting ? 'Processing...' : creditMemoStatus || 'Processing...'}
        </Typography>
      </Box>
    </Box>
  )

  if (initialLoading) {
    return (
      <Box className={classes.container}>
        <Loader />
      </Box>
    )
  }

  if (loadError && !loading) {
    return (
      <Box className={classes.container}>
        <Box style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '400px', gap: '16px' }}>
          <Warning style={{ fontSize: '3rem', color: '#f57c00' }} />
          <Typography>{strings.APPLICANT.CREDIT_MEMO.LOAD_ERROR}</Typography>
          <Button variant="outlined" onClick={() => { setLoadError(false); setShowCreditMemo(false); setTimeout(() => setShowCreditMemo(true)) }}>
            {strings.APPLICANT.CREDIT_MEMO.APPROVAL.RETRY}
          </Button>
        </Box>
      </Box>
    )
  }

  // Show processing screen while the memo is generated (before the data loader below, which would
  // otherwise show only a skeleton for as long as the request takes)
  if (requesting || isCheckingStatus || creditMemoStatus === 'IN_PROGRESS') {
    return processingScreen
  }

  if (loading || (showCreditMemo && !creditMemoData)) {
    return (
      <Box className={classes.container}>
        <Loader />
      </Box>
    )
  }


  // Show request button when credit memo hasn't been requested yet
  if (!showCreditMemo) {
    return (
      <Box className={classes.container}>
        <Box
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '400px',
            textAlign: 'center'
          }}
        >
          <Typography variant="h4" style={{ marginBottom: '16px', fontWeight: 600 }}>
            {strings.APPLICANT.CREDIT_MEMO.CREDIT_MEMO_REPORT}
          </Typography>
          <Typography
            variant="body1"
            style={{
              marginBottom: '32px',
              color: '#666',
              maxWidth: '500px',
              lineHeight: 1.6
            }}
          >
            {strings.APPLICANT.CREDIT_MEMO.GENERATE_DESCRIPTION}
          </Typography>
          <Button
            variant="contained"
            color="primary"
            size="large"
            onClick={handleRequestCreditMemo}
            disabled={loading || !hasSavedReports}
            style={{ padding: '12px 32px', fontSize: '1rem' }}
            endIcon={creditUsageEnabled && creditCosts ?
              <CreditCost cost={getCreditCost()} /> : undefined}
          >
            {loading ? strings.APPLICANT.CREDIT_MEMO.REQUESTING_CREDIT_MEMO : strings.APPLICANT.CREDIT_MEMO.REQUEST_CREDIT_MEMO}
          </Button>
          {!hasSavedReports && (
            <Box style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '16px', color: '#666' }}>
              <Info style={{ fontSize: '1.125rem' }} />
              <Typography variant="body2">
                {strings.APPLICANT.CREDIT_MEMO.NO_REPORTS_SAVED}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    )
  }

  return (
    <Box className={`${classes.container} credit-memo-content`}>
      {/* Header */}
      <Box className={classes.header}>
        <Box className={classes.headerTop}>
          <Box>
            <Typography variant="h1" className={classes.title}>
              {strings.APPLICANT.CREDIT_MEMO.CREDIT_MEMO_REPORT}
            </Typography>
            <Typography className={classes.subtitle}>
              {strings.APPLICANT.CREDIT_MEMO.COMPREHENSIVE_RISK_ASSESSMENT}
            </Typography>
          </Box>
          <Box className={classes.buttonGroup}>
            {/* With approval on, PDFs live in the approval panel (draft preview, every version, the signed copy). */}
            {!creditMemoApprovalEnabled && (
              <Button
                variant="outlined"
                size="small"
                onClick={handleExportPdf}
                loading={exportingPdf}
                startIcon={<PictureAsPdf />}
                style={{ marginRight: '8px' }}
                className="no-print"
              >
                Export PDF
              </Button>
            )}
            <Chip
              label={`${strings.APPLICANT.CREDIT_MEMO.GENERATED}: ${creditMemoData.report.generatedDate}`}
              variant="outlined"
              size="small"
              className={classes.dateBadge}
            />
          </Box>
        </Box>
      </Box>

      {creditMemoApprovalEnabled && (
        <CreditMemoApprovalPanel applicantId={applicantId} businessName={businessName} memoReady onLockChange={setApprovalLocked} />
      )}

      {/* Company Summary Section */}
      <Paper className={classes.companyCard} elevation={0}>
        <Box className={classes.cardPadding}>
          <Box className={classes.companyHeader}>
            <Box className={classes.companyIcon}>
              <Business style={{ color: 'var(--rox-lilac-ink)', fontSize: '1.5rem' }} />
            </Box>
            <Box>
              <Typography className={classes.companyTitle}>
                {creditMemoData.report.businessName}
              </Typography>
              <Typography className={classes.companySubtitle}>
                {creditMemoData.report.entityType} • {creditMemoData.report.industry}
              </Typography>
            </Box>
          </Box>

          <Grid container spacing={3} className={classes.infoGrid}>
            <Grid item xs={12} md={3}>
              <Box className={classes.infoItem}>
                <Typography className="label">{strings.APPLICANT.CREDIT_MEMO.DATE_ESTABLISHED}</Typography>
                <Typography className="value">{formatDate(creditMemoData.report.businessStartDate)}</Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={3}>
              <Box className={classes.infoItem}>
                <Typography className="label">{strings.APPLICANT.CREDIT_MEMO.ENTITY_TYPE}</Typography>
                <Typography className="value">{creditMemoData.report.entityType}</Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={3}>
              <Box className={classes.infoItem}>
                <Typography className="label">{strings.APPLICANT.CREDIT_MEMO.PRIMARY_LOCATION}</Typography>
                <Typography className="value">{creditMemoData.report.primaryLocation}</Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={3}>
              <Box className={classes.infoItem}>
                <Typography className="label">{strings.APPLICANT.CREDIT_MEMO.BUSINESS_NATURE}</Typography>
                <Typography className="value">{creditMemoData.report.businessNature}</Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Paper>

      {/* Key Findings & KPIs Section */}
      <Grid container spacing={3} className={classes.kpiGrid}>
        {creditMemoData.kpis.map((kpi, index) => (
          <Grid item xs={12} sm={6} lg={3} key={index}>
            <Paper className={classes.kpiCard} elevation={0}>
              <Box className={classes.kpiHeader}>
                {kpi.icon === 'scale' && <Security />}
                {kpi.icon === 'trending_up' && <TrendingUp />}
                {kpi.icon === 'check_circle' && <CheckCircle />}
                {kpi.icon === 'trending_down' && <TrendingDown />}
                <Typography style={{ flex: 1 }}>{kpi.title}</Typography>
                {kpi.title.includes('Risk Score') && (
                  !isEditingRiskScore ? (
                    <Tooltip title={approvalLocked ? strings.APPLICANT.CREDIT_MEMO.APPROVAL.LOCKED_TOOLTIP : ''}>
                      <span>
                        <Button
                          size="small"
                          onClick={() => setIsEditingRiskScore(true)}
                          disabled={approvalLocked}
                          style={{ minWidth: 'auto', padding: '4px' }}
                        >
                          <Edit style={{ fontSize: '1rem' }} />
                        </Button>
                      </span>
                    </Tooltip>
                  ) : (
                    <Box style={{ display: 'flex', gap: '4px' }}>
                      <Button
                        size="small"
                        onClick={handleCancelRiskScore}
                        style={{ minWidth: 'auto', padding: '4px' }}
                      >
                        <Close style={{ fontSize: '1rem' }} />
                      </Button>
                      <Button
                        size="small"
                        onClick={handleSaveRiskScore}
                        color="primary"
                        style={{ minWidth: 'auto', padding: '4px' }}
                      >
                        <Save style={{ fontSize: '1rem' }} />
                      </Button>
                    </Box>
                  )
                )}
              </Box>
              <Box>
                {kpi.title.includes('Risk Score') && isEditingRiskScore ? (
                  <TextField
                    value={editedRiskScore}
                    onChange={(e) => {
                      const value = e.target.value
                      if (value === '') {
                        setEditedRiskScore('')
                        return
                      }

                      const numValue = parseInt(value)
                      if (!isNaN(numValue) && numValue >= 0 && numValue <= 100) {
                        setEditedRiskScore(value)
                      }
                    }}
                    variant="outlined"
                    size="small"
                    type="number"
                    inputProps={{ min: 0, max: 100 }}
                    style={{ marginBottom: '16px', width: '100px' }}
                    helperText="0-100"
                  />
                ) : (
                  <Typography
                    className={classes.kpiValue}
                    style={{
                      color: kpi.color === 'error' ? 'var(--status-declined)' : kpi.color === 'success' ? 'var(--status-approved)' : '#333'
                    }}
                  >
                    {kpi.value}
                    {kpi.suffix && (
                      <Typography component="span" style={{ fontSize: '0.875rem', color: '#666', marginLeft: '4px' }}>
                        {kpi.suffix}
                      </Typography>
                    )}
                  </Typography>
                )}
                {kpi.progress && (
                  <Box className={classes.progressBar}>
                    <Box
                      className={classes.progressFill}
                      style={{
                        width: `${kpi.progress}%`,
                        backgroundColor: kpi.progress > 70 ? 'var(--status-declined)' : kpi.progress > 40 ? 'var(--status-pending)' : 'var(--status-approved)'
                      }}
                    />
                  </Box>
                )}
                <Box className={classes.kpiTrend} style={{
                  color: kpi.trend === 'error' ? 'var(--status-declined)' : kpi.trend === 'warning' ? 'var(--status-pending)' : 'var(--status-approved)'
                }}>
                  {kpi.trend === 'error' && <TrendingUp />}
                  {kpi.trend === 'warning' && <Warning />}
                  {kpi.trend === 'success' && <CheckCircle />}
                  <Typography component="span">{kpi.trendLabel}</Typography>
                </Box>
                <Typography className={classes.kpiDescription}>
                  {kpi.description}
                </Typography>
                {kpi.reasons && kpi.reasons.length > 0 && (
                  <Tooltip
                    title={
                      <Box>
                        <ul className={classes.tooltipList}>
                          {kpi.reasons.map((reason, rIdx) => (
                            <li key={rIdx}>{reason}</li>
                          ))}
                        </ul>
                      </Box>
                    }
                    placement="top"
                    arrow
                    classes={{ tooltip: classes.darkTooltip }}
                  >
                    <Box className={classes.riskFactorsContainer}>
                      <Typography variant="caption">View Risk Factors</Typography>
                      <Info />
                    </Box>
                  </Tooltip>
                )}
              </Box>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* Risk Assessment Insights */}
      <Paper className={classes.riskAssessmentCard} elevation={0}>
        <Box className={classes.cardPadding}>
          <Box style={{ marginBottom: '24px' }}>
            <Typography className={classes.sectionTitle}>{strings.APPLICANT.CREDIT_MEMO.RISK_ASSESSMENT_INSIGHTS}</Typography>
            <Typography className={classes.sectionSubtitle}>
              {strings.APPLICANT.CREDIT_MEMO.DETAILED_FINDINGS}
            </Typography>
          </Box>

          <Box style={{ marginBottom: '32px' }}>
            <Typography className={classes.subsectionTitle}>
              <Security />
              {strings.APPLICANT.CREDIT_MEMO.PRIMARY_RISK_CATEGORIES}
            </Typography>
            <Grid container spacing={2}>
              {(creditMemoData?.riskAssessment?.primaryRisk || []).map((category, idx) => (
                <Grid item xs={12} md={6} key={idx}>
                  <Box style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {category.childs.map((item, childIdx) => {
                      const statusConfig = getStatusConfig(item.status)
                      const Icon = statusConfig.icon
                      return (
                        <Box key={childIdx} className={classes.riskItemCard}>
                          <Box className={classes.riskItemLeft}>
                            <Icon style={{ color: statusConfig.bgColor, fontSize: '1rem' }} />
                            <Box className={classes.riskItemContent}>
                              <Typography className={classes.riskItemName}>{item.name}</Typography>
                              {item.entityName && (
                                <Typography className={classes.riskItemEntity}>{item.entityName}</Typography>
                              )}
                            </Box>
                          </Box>
                          <Chip
                            label={item.description}
                            variant="outlined"
                            size="small"
                            className={`${classes.statusBadge} ${
                              item.status === 'GREEN' ? classes.greenStatus :
                              item.status === 'ORANGE' ? classes.orangeStatus :
                              classes.redStatus
                            }`}
                          />
                        </Box>
                      )
                    })}
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>

          <Divider />

          <Box style={{ margin: '32px 0' }}>
            <Typography className={classes.subsectionTitle}>
              <Gavel />
              {strings.APPLICANT.CREDIT_MEMO.ADDITIONAL_RISK_FACTORS}
            </Typography>
            <Grid container spacing={2}>
              {(creditMemoData?.riskAssessment?.additionalRisk || []).map((category, idx) => (
                category.childs.map((item, childIdx) => {
                  const statusConfig = getStatusConfig(item.status)
                  const Icon = statusConfig.icon
                  return (
                    <Grid item xs={12} md={4} lg={4} key={`${idx}-${childIdx}`}>
                      <Box style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '8px',
                        padding: '12px',
                        borderRadius: '8px',
                        border: '1px solid #e0e0e0',
                        backgroundColor: 'var(--bg-canvas)',
                        transition: 'background-color 0.2s ease',
                        height: '100%'
                      }}>
                        <Box style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <Icon style={{ color: statusConfig.bgColor, fontSize: '1rem' }} />
                          <Typography className={classes.riskItemName} style={{ flex: 1 }}>{item.name}</Typography>
                        </Box>
                        {item.entityName && (
                          <Typography className={classes.riskItemEntity} style={{ paddingLeft: '24px', width: "100%" }}>
                            {item.entityName}
                          </Typography>
                        )}
                        <Box style={{ paddingLeft: '24px' }}>
                          <Chip
                            label={item.description}
                            variant="outlined"
                            size="small"
                            className={`${classes.statusBadge} ${
                              item.status === 'GREEN' ? classes.greenStatus :
                              item.status === 'ORANGE' ? classes.orangeStatus :
                              classes.redStatus
                            }`}
                          />
                        </Box>
                      </Box>
                    </Grid>
                  )
                })
              )).flat()}
            </Grid>
          </Box>
        </Box>
      </Paper>

      {/* Executive Summary Section */}
      <Paper className={classes.executiveSummaryCard} elevation={0}>
        <Box className={classes.cardPadding}>
          <Box className={classes.executiveSummaryHeader}>
            <Box>
              <Typography className={classes.sectionTitle}>{strings.APPLICANT.CREDIT_MEMO.EXECUTIVE_SUMMARY}</Typography>
              <Typography className={classes.sectionSubtitle}>
                {strings.APPLICANT.CREDIT_MEMO.COMPREHENSIVE_ANALYSIS}
              </Typography>
            </Box>
            {!isEditingExecutiveSummary ? (
              <Tooltip title={approvalLocked ? strings.APPLICANT.CREDIT_MEMO.APPROVAL.LOCKED_TOOLTIP : ''}>
                <span>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setIsEditingExecutiveSummary(true)}
                    disabled={approvalLocked}
                    startIcon={<Edit />}
                  >
                    {strings.APPLICANT.CREDIT_MEMO.EDIT}
                  </Button>
                </span>
              </Tooltip>
            ) : (
              <Box className={classes.editButtons}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleCancel}
                  startIcon={<Close />}
                >
                  {strings.APPLICANT.CREDIT_MEMO.CANCEL_EDIT}
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleSave}
                  startIcon={<Save />}
                  color="primary"
                >
                  {strings.APPLICANT.CREDIT_MEMO.SAVE}
                </Button>
              </Box>
            )}
          </Box>

          <Box style={{ marginTop: '24px' }}>
            {isEditingExecutiveSummary ? (
              <TextField
                label={strings.APPLICANT.CREDIT_MEMO.EXECUTIVE_SUMMARY}
                multiline
                rows={8}
                fullWidth
                variant="outlined"
                value={editedSummary}
                onChange={(e) => setEditedSummary(e.target.value)}
                className={classes.summaryTextfield}
              />
            ) : (
              <Box className={classes.summaryContent}>
                <Typography component="div" style={{ whiteSpace: 'pre-wrap' }}>
                  {creditMemoData.executiveSummary.content}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Paper>
    </Box>
  )
}

export default CreditMemo